import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PrezzoConvenzionato } from "@/components/accordi/PrezzoConvenzionato";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";

const INIT = { titolo: "proprietario", fabbricato: 150000, contenuto: 20000, furto_contenuto: 0, rc: true, rc_massimale: 1000000, assistenza: true };

// Casa semplificata per l'aderente: valori abitazione/contenuto, furto, RC e assistenza.
export const CalcCasa = ({ codice, onSalva, onConsulenza }) => {
  const [f, setF] = useState(INIT);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const num = (k) => (e) => setF({ ...f, [k]: Number(e.target.value || 0) });
  useEffect(() => { setRes(null); }, [f]);

  const calcola = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/public/accordi/calcola", { codice, product: "casa", input: f });
      setRes(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-5" data-testid="calc-casa">
      <div className="space-y-3 lg:col-span-3">
        <div className="flex gap-2">
          {[["proprietario", "Sono proprietario"], ["affittuario", "Sono in affitto"]].map(([v, l]) => (
            <button key={v} onClick={() => setF({ ...f, titolo: v })} data-testid={`casa-titolo-${v}`}
              className={`rounded-full border px-3.5 py-1.5 text-sm ${f.titolo === v ? "border-forest bg-forest text-white" : "border-border text-forest"}`}>{l}</button>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>{f.titolo === "proprietario" ? "Valore di ricostruzione dell'abitazione (€)" : "Rischio locativo (€)"}</Label><Input type="number" value={f.fabbricato} onChange={num("fabbricato")} data-testid="casa-fabbricato" /></div>
          <div className="space-y-1.5"><Label>Valore del contenuto (€)</Label><Input type="number" value={f.contenuto} onChange={num("contenuto")} data-testid="casa-contenuto" /></div>
          <div className="space-y-1.5"><Label>Furto del contenuto (€, 0 = no)</Label><Input type="number" value={f.furto_contenuto} onChange={num("furto_contenuto")} data-testid="casa-furto" /></div>
          <div className="space-y-1.5"><Label>Massimale RC</Label>
            <select value={f.rc_massimale} onChange={num("rc_massimale")} disabled={!f.rc} data-testid="casa-rc-massimale" className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
              {[1000000, 2000000, 3000000].map((m) => <option key={m} value={m}>€ {m.toLocaleString("it-IT")}</option>)}
            </select>
          </div>
        </div>
        <label className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm text-forest">Responsabilità civile della famiglia<Switch checked={f.rc} onCheckedChange={(v) => setF({ ...f, rc: v })} data-testid="casa-rc" /></label>
        <label className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm text-forest">Assistenza casa (idraulico, elettricista in emergenza)<Switch checked={f.assistenza} onCheckedChange={(v) => setF({ ...f, assistenza: v })} data-testid="casa-assistenza" /></label>
        <Button onClick={calcola} disabled={busy} data-testid="casa-calcola" className="bg-forest hover:bg-forest-hover">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Calcola il premio</Button>
      </div>
      <div className="space-y-3 lg:col-span-2" data-testid="casa-risultato">
        {res?.ok && <>
          <PrezzoConvenzionato r={res} testid="casa-prezzo" />
          <ul className="space-y-0.5 text-xs text-muted-foreground">{res.garanzie.map((g) => <li key={g}>• {g}</li>)}</ul>
          <Button onClick={() => onSalva(f)} data-testid="casa-salva" className="w-full bg-terracotta hover:bg-terracotta-hover">Salva il preventivo</Button>
        </>}
        {res && !res.ok && (
          <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="casa-consulenza-richiesta">
            <div className="flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0" />{res.errore}</div>
            <Button variant="outline" onClick={onConsulenza} data-testid="casa-richiedi-consulenza" className="w-full">Richiedi una consulenza dedicata</Button>
          </div>
        )}
      </div>
    </div>
  );
};
